import React, { useState, useEffect } from 'react';

import { EditOutlined, SaveOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import CurrencySelectImpl from '@components/shared/selects/CurrencySelect';
import FieldSectionImpl from '@components/shared/FieldSection';
import RateCardTabImpl from '@components/shared/rate_cards/RateCardTab';
import UserInvitationsListImpl from '@components/shared/user_invitations/UserInvitationsList';
import ListPageHeader from '@shared/ListPageHeader';
import Can from '@shared/permissions/Can';
import { Card, Form, Input, Switch, Button, Space, message, Spin, Row, Col, Descriptions, Tabs, Select, Tooltip, Modal, Tag, Divider } from 'antd';
import type { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { operatorApiFetch } from '@services/operator/apiService';
import { userInvitationService } from '@services/operator/userInvitationService';
import { useFeatureEnabled, useIsStandard } from '@contexts/operator/PlanContext';

interface Warehouse {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface Merchant {
  id: string;
  name: string;
  activated?: boolean;
  address?: string | null;
  state?: string | null;
  city?: string | null;
  district?: string | null;
  postal_code?: string | null;
  phone?: string | null;
  vat_number?: string | null;
  default_currency?: string | null;
  inventory_traceability_mode?: string | null;
  serial_tracking_policy?: string | null;
  warehouse_ids?: string[];
  default_warehouse_id?: string | null;
  enable_de_identification?: boolean;
  de_identify_days?: number | null;
  de_identify_mechanism?: string | null;
  default_allow_partial_allocation?: boolean;
  shelves_visible?: boolean;
  fulfillment_lot_policy?: string;
  expiration_fallback_enabled?: boolean;
  unknown_product_policy?: string | null;
  // 寄件人資訊政策
  sender_name_policy?: string;
  sender_phone_policy?: string;
  sender_address_policy?: string;
  custom_sender_name?: string | null;
  custom_sender_phone?: string | null;
  custom_sender_address?: string | null;
  custom_sender_postal_code?: string | null;
  custom_sender_state?: string | null;
  custom_sender_city?: string | null;
  custom_sender_district?: string | null;
  // SLA
  sla_enabled?: boolean;
  sla_cutoff_time?: string | null;
  sla_grace_period_minutes?: number | null;
  sla_target_percentage?: number | string | null;
  // misc
  editable?: boolean;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

// ============================================
// Info tab 額外設定欄位配置
// ============================================
const INFO_FIELD_CONFIGS = {
  deIdentification: {
    title: 'fee_sections.de_identification',
    tooltip: 'tooltips.de_identification',
    fields: [
      { key: 'enable_de_identification', label: 'fields.enable_de_identification', type: 'boolean', tooltip: 'tooltips.enable_de_identification' },
      // 啟用去識別化後必須指定「幾天後」與「怎麼處理」，否則功能無法執行（silent no-op）。
      { key: 'de_identify_days', label: 'fields.de_identify_days', type: 'number', tooltip: 'tooltips.de_identify_days', requiredIf: { enable_de_identification: true } },
      { key: 'de_identify_mechanism', label: 'fields.de_identify_mechanism', type: 'select', options: ['masked', 'deleted'], optionsPrefix: 'de_identify_mechanisms', tooltip: 'tooltips.de_identify_mechanism', requiredIf: { enable_de_identification: true } },
    ],
  },
  featureToggles: {
    title: 'fee_sections.feature_toggles',
    fields: [
      { key: 'default_allow_partial_allocation', label: 'fields.default_allow_partial_allocation', type: 'boolean', tooltip: 'tooltips.default_allow_partial_allocation' },
      { key: 'shelves_visible', label: 'fields.shelves_visible', type: 'boolean' },
      { key: 'fulfillment_lot_policy', label: 'fields.fulfillment_lot_policy', type: 'select', options: ['flexible', 'strict'], optionsPrefix: 'fulfillment_lot_policies', required: true, tooltip: 'tooltips.fulfillment_lot_policy' },
      { key: 'serial_tracking_policy', label: 'fields.serial_tracking_policy', type: 'select', options: ['outbound_only', 'full_lifecycle'], optionsPrefix: 'serial_tracking_policies', required: true, tooltip: 'tooltips.serial_tracking_policy' },
      { key: 'expiration_fallback_enabled', label: 'fields.expiration_fallback_enabled', type: 'boolean', tooltip: 'tooltips.expiration_fallback_enabled' },
      { key: 'unknown_product_policy', label: 'fields.unknown_product_policy', type: 'select', options: ['auto_create', 'reject_order'], optionsPrefix: 'unknown_product_policies' },
    ],
  },
  senderInfo: {
    title: 'fee_sections.sender_info',
    tooltip: 'tooltips.sender_info',
    fields: [
      // 寄件人姓名直接以 input 呈現：use_merchant 預填 merchant.name；其他情況預填 custom_sender_name。
      // 儲存時若輸入值等於 merchant.name 則回寫成 use_merchant 並清空 custom_sender_name；否則寫成 custom + 該值。
      { key: 'custom_sender_name', label: 'fields.sender_name', type: 'text',
        required: true },
      // spacer：佔住右半邊，讓下一個欄位換到下一列（顯示模式下不出現）
      { key: '__sender_name_spacer', showIf: { __never: true }, hideInDisplay: true },

      { key: 'sender_phone_policy', label: 'fields.sender_phone_policy', type: 'select',
        options: ['use_merchant', 'use_warehouse', 'custom'], optionsPrefix: 'sender_policies',
        required: true, tooltip: 'tooltips.sender_phone_policy',
        appendCustomFrom: 'custom_sender_phone' },
      { key: 'custom_sender_phone', label: 'fields.custom_sender_phone', type: 'text',
        showIf: { sender_phone_policy: 'custom' }, requiredIf: { sender_phone_policy: 'custom' },
        hideInDisplay: true },

      { key: 'sender_address_policy', label: 'fields.sender_address_policy', type: 'select',
        options: ['use_merchant', 'use_warehouse', 'custom'], optionsPrefix: 'sender_policies',
        required: true, tooltip: 'tooltips.sender_address_policy',
        appendCustomFrom: ['custom_sender_postal_code', 'custom_sender_state', 'custom_sender_city', 'custom_sender_district', 'custom_sender_address'] },
      // 寄件人客製地址：全部靠右堆疊，與原本（只有 postal_code/city/address 時）layout 一致
      { key: 'custom_sender_state', label: 'fields.custom_sender_state', type: 'text',
        showIf: { sender_address_policy: 'custom' },
        hideInDisplay: true, hidePlaceholder: true },
      { key: 'custom_sender_city', label: 'fields.custom_sender_city', type: 'text',
        offset: 12,
        showIf: { sender_address_policy: 'custom' },
        hideInDisplay: true, hidePlaceholder: true },
      { key: 'custom_sender_district', label: 'fields.custom_sender_district', type: 'text',
        offset: 12,
        showIf: { sender_address_policy: 'custom' },
        hideInDisplay: true, hidePlaceholder: true },
      { key: 'custom_sender_postal_code', label: 'fields.custom_sender_postal_code', type: 'text',
        offset: 12,
        showIf: { sender_address_policy: 'custom' },
        hideInDisplay: true, hidePlaceholder: true },
      { key: 'custom_sender_address', label: 'fields.custom_sender_address', type: 'text',
        offset: 12,
        showIf: { sender_address_policy: 'custom' }, requiredIf: { sender_address_policy: 'custom' },
        hideInDisplay: true, hidePlaceholder: true },
    ],
  },
  slaConfiguration: {
    title: 'fee_sections.sla_configuration',
    tooltip: 'tooltips.sla_configuration',
    fields: [
      { key: 'sla_enabled', label: 'fields.sla_enabled', type: 'boolean' },
      { key: 'sla_cutoff_time', label: 'fields.sla_cutoff_time', type: 'time' },
      // 啟用 SLA 後必須指定緩衝分鐘數與目標合規率，否則系統無法計算是否達標。
      { key: 'sla_grace_period_minutes', label: 'fields.sla_grace_period_minutes', type: 'number', tooltip: 'tooltips.sla_grace_period_minutes', requiredIf: { sla_enabled: true } },
      { key: 'sla_target_percentage', label: 'fields.sla_target_percentage', type: 'number', requiredIf: { sla_enabled: true } },
    ],
  },
};

interface SlaSectionProps {
  slaAvailable: boolean;
  merchant: Merchant | null;
  form?: FormInstance;
  editMode: boolean;
}

const SlaSection = ({ slaAvailable, merchant, form, editMode }: SlaSectionProps) => {
  // FieldSection 仍是 .jsx；TS 從其 destructure 推導出的 props 不完整，這裡 cast 保留 runtime 行為。
  const FieldSection = FieldSectionImpl as React.ComponentType<any>;
  return (
    <FieldSection
      config={INFO_FIELD_CONFIGS.slaConfiguration}
      data={merchant}
      form={form}
      editMode={editMode}
      column={2}
      disabled={!slaAvailable}
    />
  );
};

const MerchantShow: React.FC = () => {
  // .jsx 子元件 cast 為 ComponentType<any> 處理 TS prop 推導不完整；放在 component body 內避免 Vite HMR top-level TDZ。
  const CurrencySelect = CurrencySelectImpl as React.ComponentType<any>;
  const FieldSection = FieldSectionImpl as React.ComponentType<any>;
  const RateCardTab = RateCardTabImpl as React.ComponentType<any>;
  const UserInvitationsList = UserInvitationsListImpl as React.ComponentType<any>;

  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const slaAvailable = useFeatureEnabled('orders.sla') as boolean;
  const isStandard = useIsStandard() as boolean;

  // Standard 方案隱藏 shelves_visible 欄位
  const featureTogglesConfig = isStandard
    ? { ...INFO_FIELD_CONFIGS.featureToggles, fields: INFO_FIELD_CONFIGS.featureToggles.fields.filter(f => f.key !== 'shelves_visible') }
    : INFO_FIELD_CONFIGS.featureToggles;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [availableWarehouses, setAvailableWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>([]);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);

  // 顯示模式下，senderInfo 區塊的 custom_sender_name 改以 input 呈現語意：
  // policy=use_merchant 時實際 DB 沒存值，要從 merchant.name 解析出來給 FieldSection 顯示。
  const senderInfoData = merchant
    ? {
        ...merchant,
        custom_sender_name: merchant.sender_name_policy === 'use_merchant'
          ? merchant.name
          : merchant.custom_sender_name,
      }
    : merchant;

  // Watch warehouse_ids changes to filter default warehouse options
  const watchedWarehouseIds = Form.useWatch<string[] | undefined>('warehouse_ids', form);
  const watchedDefaultWarehouseId = Form.useWatch<string | undefined>('default_warehouse_id', form);
  const warehouseFieldsValid = (watchedWarehouseIds?.length || 0) > 0 && !!watchedDefaultWarehouseId;

  useEffect(() => {
    fetchMerchant();
    fetchWarehouses();
  }, [id]);

  useEffect(() => {
    if (watchedWarehouseIds) {
      setSelectedWarehouses(watchedWarehouseIds);
      const currentDefault = form.getFieldValue('default_warehouse_id');
      const isValidDefault = currentDefault && watchedWarehouseIds.includes(currentDefault);
      if (!isValidDefault) {
        form.setFieldValue(
          'default_warehouse_id',
          watchedWarehouseIds.length > 0 ? watchedWarehouseIds[0] : null,
        );
      }
    }
  }, [watchedWarehouseIds]);

  const fetchWarehouses = async () => {
    try {
      const response = await operatorApiFetch(`warehouses?page_size=100`);
      const data = await response.json();
      if (data.success) {
        setAvailableWarehouses(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch warehouses:', error);
    }
  };

  const fetchMerchant = async () => {
    try {
      setLoading(true);
      const response = await operatorApiFetch(`merchants/${id}`);
      const data = await response.json();

      if (data.success && data.data) {
        setMerchant(data.data);
        const initialWarehouseIds = data.data.warehouse_ids || [];

        // 基本資訊表單
        form.setFieldsValue({
          name: data.data.name,
          activated: data.data.activated,
          address: data.data.address,
          state: data.data.state,
          city: data.data.city,
          district: data.data.district,
          postal_code: data.data.postal_code,
          phone: data.data.phone,
          vat_number: data.data.vat_number,
          default_currency: data.data.default_currency || 'TWD',
          inventory_traceability_mode: data.data.inventory_traceability_mode || 'aggregated',
          serial_tracking_policy: data.data.serial_tracking_policy || 'outbound_only',
          warehouse_ids: initialWarehouseIds,
          default_warehouse_id: data.data.default_warehouse_id,
          // 去識別化 & 功能開關
          enable_de_identification: data.data.enable_de_identification,
          de_identify_days: data.data.de_identify_days,
          de_identify_mechanism: data.data.de_identify_mechanism,
          default_allow_partial_allocation: data.data.default_allow_partial_allocation,
          shelves_visible: data.data.shelves_visible,
          fulfillment_lot_policy: data.data.fulfillment_lot_policy,
          expiration_fallback_enabled: data.data.expiration_fallback_enabled,
          unknown_product_policy: data.data.unknown_product_policy,
          // 寄件人資訊政策
          sender_name_policy: data.data.sender_name_policy,
          sender_phone_policy: data.data.sender_phone_policy,
          sender_address_policy: data.data.sender_address_policy,
          // 寄件人姓名以 input 呈現：use_merchant 預填 merchant.name；其他預填 custom_sender_name
          custom_sender_name: data.data.sender_name_policy === 'use_merchant'
            ? data.data.name
            : data.data.custom_sender_name,
          custom_sender_phone: data.data.custom_sender_phone,
          custom_sender_address: data.data.custom_sender_address,
          custom_sender_postal_code: data.data.custom_sender_postal_code,
          custom_sender_state: data.data.custom_sender_state,
          custom_sender_city: data.data.custom_sender_city,
          custom_sender_district: data.data.custom_sender_district,
          // SLA
          sla_enabled: data.data.sla_enabled,
          sla_cutoff_time: data.data.sla_cutoff_time,
          sla_grace_period_minutes: data.data.sla_grace_period_minutes,
          sla_target_percentage: data.data.sla_target_percentage ? Number(data.data.sla_target_percentage) : null,
        });
        setSelectedWarehouses(initialWarehouseIds);
      } else {
        throw new Error('Failed to fetch merchant');
      }
    } catch (error) {
      console.error('Failed to fetch merchant:', error);
      message.error(t('operator.merchants.show.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // SLA enabled requires cutoff time
      if (values.sla_enabled && !values.sla_cutoff_time) {
        form.setFields([{
          name: 'sla_cutoff_time',
          errors: [t('operator.merchants.show.sla_settings.cutoff_time_required') as string],
        }]);
        return;
      }

      // 寄件人姓名以 input 呈現，回寫時對照 merchant.name 推 policy：
      // 與 merchant.name 相同 → use_merchant + 清空 custom_sender_name；不同 → custom + 寫入該值。
      const senderName = values.custom_sender_name;
      if (senderName != null && senderName === merchant?.name) {
        values.sender_name_policy = 'use_merchant';
        values.custom_sender_name = null;
      } else {
        values.sender_name_policy = 'custom';
      }

      setSaving(true);

      const wasSlaEnabled = merchant?.sla_enabled;
      const isSlaBeingEnabled = !wasSlaEnabled && values.sla_enabled;

      const response = await operatorApiFetch(`merchants/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ merchant: values }),
      });

      const data = await response.json();

      if (data.success) {
        message.success(t('operator.merchants.show.messages.update_success'));
        setEditMode(false);
        fetchMerchant();

        // When SLA is newly enabled, ask if user wants to backfill historical orders
        if (isSlaBeingEnabled) {
          Modal.confirm({
            title: t('operator.merchants.show.sla_settings.backfill_confirm_title'),
            content: t('operator.merchants.show.sla_settings.backfill_confirm_content'),
            okText: t('common.button.confirm'),
            cancelText: t('common.button.skip'),
            onOk: async () => {
              try {
                const backfillRes = await operatorApiFetch(`merchants/${id}/sla_settings/backfill`, {
                  method: 'POST',
                });
                const backfillData = await backfillRes.json();
                if (backfillData.success) {
                  message.success(t('operator.merchants.show.sla_settings.backfill_started'));
                } else {
                  message.error(backfillData.message || t('common.error.fetch_failed'));
                }
              } catch (err) {
                console.error('Failed to backfill SLA:', err);
                message.error(t('common.error.fetch_failed'));
              }
            },
          });
        }
      } else {
        throw new Error(data.message || 'Failed to update merchant');
      }
    } catch (error) {
      console.error('Failed to update merchant:', error);
      message.error(t('operator.merchants.show.error.update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!merchant) return;
    form.setFieldsValue({
      name: merchant.name,
      activated: merchant.activated,
      address: merchant.address,
      state: merchant.state,
      city: merchant.city,
      district: merchant.district,
      postal_code: merchant.postal_code,
      phone: merchant.phone,
      vat_number: merchant.vat_number,
      default_currency: merchant.default_currency || 'TWD',
      inventory_traceability_mode: merchant.inventory_traceability_mode || 'aggregated',
      serial_tracking_policy: merchant.serial_tracking_policy || 'outbound_only',
      warehouse_ids: merchant.warehouse_ids || [],
      default_warehouse_id: merchant.default_warehouse_id,
      enable_de_identification: merchant.enable_de_identification,
      de_identify_days: merchant.de_identify_days,
      de_identify_mechanism: merchant.de_identify_mechanism,
      default_allow_partial_allocation: merchant.default_allow_partial_allocation,
      shelves_visible: merchant.shelves_visible,
      fulfillment_lot_policy: merchant.fulfillment_lot_policy,
      expiration_fallback_enabled: merchant.expiration_fallback_enabled,
      unknown_product_policy: merchant.unknown_product_policy,
      // 寄件人資訊政策
      sender_name_policy: merchant.sender_name_policy,
      sender_phone_policy: merchant.sender_phone_policy,
      sender_address_policy: merchant.sender_address_policy,
      custom_sender_name: merchant.sender_name_policy === 'use_merchant'
        ? merchant.name
        : merchant.custom_sender_name,
      custom_sender_phone: merchant.custom_sender_phone,
      custom_sender_address: merchant.custom_sender_address,
      custom_sender_postal_code: merchant.custom_sender_postal_code,
      custom_sender_state: merchant.custom_sender_state,
      custom_sender_city: merchant.custom_sender_city,
      custom_sender_district: merchant.custom_sender_district,
      // SLA
      sla_enabled: merchant.sla_enabled,
      sla_cutoff_time: merchant.sla_cutoff_time,
      sla_grace_period_minutes: merchant.sla_grace_period_minutes,
      sla_target_percentage: merchant.sla_target_percentage ? Number(merchant.sla_target_percentage) : null,
    });
    setEditMode(false);
  };

  const handleEditClick = () => {
    setEditMode(true);
  };

  const handleTabChange = (key: string) => {
    if (activeTab === 'info' && editMode) {
      Modal.confirm({
        title: t('common.confirm.unsaved_title'),
        content: t('common.confirm.unsaved_content'),
        okText: t('common.button.leave'),
        cancelText: t('common.button.stay'),
        onOk: () => {
          handleCancel();
          setActiveTab(key);
        },
      });
    } else {
      setActiveTab(key);
    }
  };

  if (loading || !merchant) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const tabItems = [
    {
      key: 'info',
      label: t('operator.merchants.show.tabs.info'),
      children: editMode ? (
        <Form form={form} layout="vertical" onFinish={handleSave}>
          {/* Row 1: name | status */}
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t('operator.merchants.show.fields.name')}
                rules={[{ required: true, message: t('operator.merchants.show.validation.name_required') as string }]}
              >
                <Input placeholder={t('operator.merchants.show.placeholders.name') as string} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="activated" label={t('operator.merchants.show.fields.status')} valuePropName="checked">
                <Switch
                  checkedChildren={t('common.status.active')}
                  unCheckedChildren={t('common.status.inactive')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="default_currency"
                label={t('operator.merchants.show.fields.default_currency')}
                rules={[{ required: true, message: t('common.validation.required') as string }]}
              >
                <CurrencySelect apiFetch={operatorApiFetch} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label={t('operator.merchants.show.fields.phone')}>
                <Input placeholder={t('operator.merchants.show.placeholders.phone') as string} />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2a: state | city | district */}
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item name="state" label={t('models.merchant.state') as string}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="city" label={t('operator.merchants.show.fields.city')}>
                <Input placeholder={t('operator.merchants.show.placeholders.city') as string} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="district" label={t('models.merchant.district') as string}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2b: postal_code | address */}
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item name="postal_code" label={t('operator.merchants.show.fields.postal_code')}>
                <Input placeholder={t('operator.merchants.show.placeholders.postal_code') as string} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="address" label={t('operator.merchants.show.fields.address')}>
                <Input placeholder={t('operator.merchants.show.placeholders.address') as string} />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '12px 0 24px 0' }} />

          {/* Row 3: inventory_traceability_mode */}
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="inventory_traceability_mode"
                label={
                  <Space>
                    {t('operator.merchants.show.fields.inventory_traceability_mode')}
                    <Tooltip title={t('operator.merchants.show.tooltips.inventory_traceability_mode')} overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 400 }}>
                      <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                    </Tooltip>
                  </Space>
                }
              >
                <Select>
                  <Select.Option value="aggregated">{t('operator.merchants.show.traceability_modes.aggregated')}</Select.Option>
                  <Select.Option value="isolated">{t('operator.merchants.show.traceability_modes.isolated')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Row 4: assigned_warehouses | default_warehouse */}
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="warehouse_ids"
                label={t('operator.merchants.show.fields.assigned_warehouses')}
                extra={t('operator.merchants.show.hints.assigned_warehouses') as React.ReactNode}
                rules={[{ required: true, type: 'array', min: 1, message: t('common.validation.required') as string }]}
              >
                <Select
                  mode="multiple"
                  placeholder={t('operator.merchants.show.placeholders.select_warehouses') as string}
                  optionFilterProp="children"
                >
                  {availableWarehouses.map((wh: Warehouse) => (
                    <Select.Option key={wh.id} value={wh.id}>{wh.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="default_warehouse_id"
                label={t('operator.merchants.show.fields.default_warehouse')}
                extra={t('operator.merchants.show.hints.default_warehouse') as React.ReactNode}
                dependencies={['warehouse_ids']}
                rules={[{ required: true, message: t('common.validation.required') as string }]}
              >
                <Select
                  placeholder={t('operator.merchants.show.placeholders.select_default_warehouse') as string}
                  allowClear
                  disabled={!selectedWarehouses || selectedWarehouses.length === 0}
                >
                  {availableWarehouses
                    .filter((wh: Warehouse) => selectedWarehouses.includes(wh.id))
                    .map((wh: Warehouse) => (
                      <Select.Option key={wh.id} value={wh.id}>{wh.name}</Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '12px 0 24px 0' }} />

          {/* De-identification & Feature Toggles */}
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <FieldSection config={INFO_FIELD_CONFIGS.deIdentification} data={merchant} form={form} editMode column={1} />
            </Col>
            <Col span={12}>
              <FieldSection config={featureTogglesConfig} data={merchant} form={form} editMode column={2} />
            </Col>
          </Row>

          {/* 寄件人資訊政策 */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={24}>
              <FieldSection config={INFO_FIELD_CONFIGS.senderInfo} data={senderInfoData} form={form} editMode column={2} />
            </Col>
          </Row>

          {/* SLA Configuration — standard 方案隱藏 */}
          {!isStandard && (
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={24}>
                <SlaSection slaAvailable={slaAvailable} merchant={merchant} form={form} editMode={true} />
              </Col>
            </Row>
          )}

          <Form.Item style={{ marginTop: 16 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />} disabled={!warehouseFieldsValid}>
                {t('common.button.save')}
              </Button>
              <Button onClick={handleCancel}>
                {t('common.button.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      ) : (
        <Row gutter={[16, 16]}>
          {/* 基本資料 */}
          <Col span={12}>
            <Card size="small" title={t('operator.merchants.show.info_sections.basic')} style={{ height: '100%' }} headStyle={{ backgroundColor: '#fafafa', fontWeight: 'normal' }}>
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={t('operator.merchants.show.fields.name')}>{merchant.name}</Descriptions.Item>
                <Descriptions.Item label={t('operator.merchants.show.fields.default_currency')}>{merchant.default_currency}</Descriptions.Item>
                <Descriptions.Item label={t('operator.merchants.show.fields.status')}>
                  {merchant.activated ? <Tag color="success">{t('common.status.active')}</Tag> : <Tag color="error">{t('common.status.inactive')}</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label={t('operator.merchants.show.fields.phone')}>{merchant.phone || '-'}</Descriptions.Item>
                <Descriptions.Item label={t('models.merchant.state')}>{merchant.state || '-'}</Descriptions.Item>
                <Descriptions.Item label={t('operator.merchants.show.fields.city')}>{merchant.city || '-'}</Descriptions.Item>
                <Descriptions.Item label={t('models.merchant.district')}>{merchant.district || '-'}</Descriptions.Item>
                <Descriptions.Item label={t('operator.merchants.show.fields.postal_code')}>{merchant.postal_code || '-'}</Descriptions.Item>
                <Descriptions.Item label={t('operator.merchants.show.fields.address')}>{merchant.address || '-'}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* 倉庫與計費設定 */}
          <Col span={12}>
            <Card size="small" title={t('operator.merchants.show.info_sections.warehouse_billing')} style={{ height: '100%' }} headStyle={{ backgroundColor: '#fafafa', fontWeight: 'normal' }}>
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={t('operator.merchants.show.fields.inventory_traceability_mode')}>
                  <Tag color={merchant.inventory_traceability_mode === 'isolated' ? 'purple' : 'green'}>
                    {t(`operator.merchants.show.traceability_modes.${merchant.inventory_traceability_mode || 'aggregated'}`) as string}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('operator.merchants.show.fields.assigned_warehouses')}>
                  {merchant.warehouse_ids && availableWarehouses.length > 0
                    ? availableWarehouses
                      .filter((wh: Warehouse) => merchant.warehouse_ids!.includes(wh.id))
                      .map((wh: Warehouse) => <Tag key={wh.id} style={{ marginBottom: 4 }}>{wh.name}</Tag>)
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('operator.merchants.show.fields.default_warehouse')}>
                  {merchant.default_warehouse_id && availableWarehouses.length > 0
                    ? <Tag color="orange">{availableWarehouses.find((wh: Warehouse) => wh.id === merchant.default_warehouse_id)?.name || '-'}</Tag>
                    : '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* 去識別化 & 功能開關 */}
          <Col span={12}>
            <FieldSection config={INFO_FIELD_CONFIGS.deIdentification} data={merchant} editMode={false} column={1} />
          </Col>
          <Col span={12}>
            <FieldSection config={featureTogglesConfig} data={merchant} editMode={false} column={2} />
          </Col>

          {/* 寄件人資訊政策 */}
          <Col span={24}>
            <FieldSection config={INFO_FIELD_CONFIGS.senderInfo} data={senderInfoData} editMode={false} column={2} />
          </Col>

          {/* SLA 設定 — standard 方案隱藏 */}
          {!isStandard && (
            <Col span={24}>
              <SlaSection slaAvailable={slaAvailable} merchant={merchant} editMode={false} />
            </Col>
          )}

          {/* 時間戳記 */}
          <Col span={24}>
            <Card size="small" title={t('operator.merchants.show.info_sections.timestamps')} headStyle={{ backgroundColor: '#fafafa', fontWeight: 'normal' }}>
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label={t('common.created_at')}>
                  {merchant.created_at ? new Date(merchant.created_at).toLocaleString() : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('common.updated_at')}>
                  {merchant.updated_at ? new Date(merchant.updated_at).toLocaleString() : '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      )
    },
    {
      key: 'fees',
      label: t('operator.merchants.show.tabs.fees'),
      children: <RateCardTab merchantId={id} editable={merchant.editable} />
    },
    {
      key: 'invitations',
      label: (
        <Space>
          {t('operator.merchants.show.tabs.invitations')}
          <Tooltip title={t('operator.merchants.show.tooltips.invitations_tab')} overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 420 }}>
            <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
          </Tooltip>
        </Space>
      ),
      children: (
        <UserInvitationsList
          userInvitationService={userInvitationService}
          merchantId={id}
          hideCard
          modalVisible={inviteModalVisible}
          onModalVisibleChange={setInviteModalVisible}
        />
      )
    },
  ];

  const isCurrentTabEditing = activeTab === 'info' && editMode;

  const showEditButton = merchant.editable &&
    activeTab === 'info' &&
    !isCurrentTabEditing;

  const renderExtraButton = () => {
    if (showEditButton) {
      return (
        <Can requiredPermission="merchants:write">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={handleEditClick}
          >
            {t('common.button.edit')}
          </Button>
        </Can>
      );
    }
    if (activeTab === 'invitations') {
      return (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setInviteModalVisible(true)}
        >
          {t('common.merchants.invitations.actions.create')}
        </Button>
      );
    }
    return null;
  };

  return (
    <div>
      <ListPageHeader
        items={[
          isStandard
            ? { label: t('operator.menu.merchant_settings.my_merchant') }
            : 'merchants',
          { label: merchant.name },
        ]}
        actions={renderExtraButton()}
      />
      <Tabs items={tabItems} activeKey={activeTab} onChange={handleTabChange} />
    </div>
  );
};

export default MerchantShow;
